import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import localforage from 'localforage';
import { Clock, Calendar, ChevronLeft, ChevronRight, Filter, UserCheck, UserX, 
  AlertTriangle, Bell, Grid, Download, Search, RefreshCw, Settings, X, 
  CheckCircle2, XCircle, User, Users, ArrowDown, ArrowUp, AlignJustify } from 'lucide-react';

// StatusDashboard component import (used inside this integration component)
import StatusDashboard from './StatusDashboard';

const StatusDashboardIntegration = ({ userRole }) => {
  // State variables
  const [showDashboard, setShowDashboard] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [statsData, setStatsData] = useState({
    missingCount: 0,
    lateCount: 0,
    checkedOutCount: 0
  });
  const [hasUnresolved, setHasUnresolved] = useState(false);

  // Base URL for API calls
  const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch';

  // Effect to get token from storage
  useEffect(() => {
    const getToken = async () => {
      try {
        const storedToken = await localforage.getItem('token');
        setToken(storedToken);
      } catch (error) {
        console.error('Error retrieving token:', error);
      }
    };
    
    getToken();
  }, []);

  // Effect to fetch stats data periodically
  useEffect(() => {
    if (!token || userRole !== 'ROLE_ADMIN') return;
    
    const fetchStats = async () => {
      try {
        setLoading(true);
        
        // Fetch missing users stats
        const response = await fetch(
          `${BASE_URL}/api/auth/missing-users`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch status data');
        }
        
        const data = await response.json();
        
        if (data) {
          setStatsData({
            missingCount: data.count || 0,
            lateCount: data.lateCount || 0,
            checkedOutCount: data.checkedOutCount || 0
          });
          
          // Set hasUnresolved if there are any missing or late users
          setHasUnresolved((data.count || 0) > 0 || (data.lateCount || 0) > 0);
        }
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };
    
    // Initial fetch
    fetchStats();
    
    // Set up interval for periodic updates (every 30 seconds)
    const interval = setInterval(fetchStats, 30000);
    
    // Clean up interval on unmount
    return () => clearInterval(interval);
  }, [token, userRole]);

  // Toggle dashboard visibility
  const toggleDashboard = () => {
    setShowDashboard(!showDashboard);
  };

  // Only show for admin users
  if (userRole !== 'ROLE_ADMIN') return null;

  return (
    <div>
      {/* Status indicator button with pulse animation */}
      <motion.div
        className="status-indicator-wrapper"
        style={{
          position: 'relative',
          display: 'inline-block',
          marginRight: '16px'
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <motion.button
          onClick={toggleDashboard}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: hasUnresolved ? '#fee2e2' : '#d1fae5',
            color: hasUnresolved ? '#ef4444' : '#10b981',
            border: 'none',
            borderRadius: '10px',
            fontWeight: '500',
            fontSize: '14px',
            cursor: 'pointer',
            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease',
            animation: hasUnresolved ? 'pulse 2s infinite' : 'none'
          }}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <Clock size={16} />
            {hasUnresolved && (
              <motion.div
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: '#ef4444',
                  border: '2px solid white'
                }}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 15
                }}
              />
            )}
          </motion.div>
          <span>Status Dashboard</span>
        </motion.button>
        
        {/* Stats badge - only show if there are issues */}
        {(statsData.missingCount > 0 || statsData.lateCount > 0) && (
          <motion.div
            style={{
              position: 'absolute',
              top: '-10px',
              right: '-10px',
              backgroundColor: '#ef4444',
              color: 'white',
              borderRadius: '12px',
              padding: '3px 8px',
              fontSize: '12px',
              fontWeight: 'bold',
              boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
              border: '2px solid white',
              zIndex: 1
            }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              type: 'spring',
              stiffness: 500,
              damping: 15,
              delay: 0.2
            }}
          >
            {statsData.missingCount + statsData.lateCount}
          </motion.div>
        )}
      </motion.div>

      {/* Render the Dashboard modal when showDashboard is true */}
      <AnimatePresence>
        {showDashboard && (
          <StatusDashboard
            onClose={() => setShowDashboard(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default StatusDashboardIntegration;